import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { ChevronRight, ChevronDown, FolderOpen, Folder, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export default function PublicCollectionNav({ 
  collections = [], 
  selectedCollection, 
  onSelectCollection,
  theme = 'minimalist',
  navTitle = 'Browse Collections',
  allProductsLabel = 'All Products',
  productCounts = {},
  isMobile = false
}) {
  const [expandedCollections, setExpandedCollections] = useState(new Set());
  const [isOpen, setIsOpen] = useState(!isMobile);
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const toggleExpanded = (collectionId) => {
    const newExpanded = new Set(expandedCollections);
    if (newExpanded.has(collectionId)) {
      newExpanded.delete(collectionId);
    } else {
      newExpanded.add(collectionId);
    }
    setExpandedCollections(newExpanded);
  };
  
  // Build tree structure
  const buildTree = () => {
    const tree = [];
    const map = {};
    
    collections.forEach(col => {
      map[col.collection_id] = { ...col, children: [] };
    });
    
    collections.forEach(col => {
      if (col.parent_id && map[col.parent_id]) {
        map[col.parent_id].children.push(map[col.collection_id]);
      } else if (!col.parent_id) {
        tree.push(map[col.collection_id]);
      }
    });
    
    return tree;
  };
  
  const renderCollection = (collection, level = 0) => {
    const isExpanded = expandedCollections.has(collection.collection_id);
    const hasChildren = collection.children && collection.children.length > 0;
    const isSelected = selectedCollection === collection.collection_id;
    const count = productCounts[collection.collection_id] || 0;
    
    // Theme-specific styles
    const themeStyles = {
      minimalist: {
        container: `hover:bg-zinc-100 ${isSelected ? 'bg-zinc-100 border-l-2 border-zinc-900' : ''}`,
        text: 'text-zinc-950',
        icon: isSelected ? 'text-zinc-900' : 'text-zinc-600'
      },
      hypebeast: {
        container: `hover:bg-zinc-900/10 ${isSelected ? 'bg-lime-400/20 border-l-4 border-lime-400' : ''}`,
        text: 'text-white',
        icon: isSelected ? 'text-lime-400' : 'text-white/70'
      },
      curator: {
        container: `hover:bg-amber-100 ${isSelected ? 'bg-amber-100 border-l-2 border-rose-800' : ''}`,
        text: 'text-amber-950',
        icon: isSelected ? 'text-rose-800' : 'text-amber-800'
      }
    };
    
    const style = themeStyles[theme] || themeStyles.minimalist;
    
    return (
      <div key={collection.collection_id} className="select-none">
        <div
          data-testid={`public-collection-${collection.collection_id}`}
          className={`
            flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-all
            ${style.container}
          `}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => {
            onSelectCollection(collection.collection_id);
            if (isMobile) setIsOpen(false);
          }}
        >
          {/* Expand/Collapse Button */}
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                toggleExpanded(collection.collection_id);
              }}
              className={`p-0.5 rounded hover:bg-black/10 ${style.icon}`}
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          ) : (
            <div className="w-4 h-4" />
          )}
          
          {/* Folder Icon */}
          {isExpanded ? (
            <FolderOpen className={`w-4 h-4 ${style.icon}`} />
          ) : (
            <Folder className={`w-4 h-4 ${style.icon}`} />
          )}
          
          {/* Collection Name */}
          <div className={`flex-1 text-sm font-medium truncate ${style.text}`}>
            {collection.name}
          </div>
          
          {/* Product Count */}
          {count > 0 && (
            <span className={`text-xs opacity-60 ${style.text}`}>
              {count}
            </span>
          )}
        </div>
        
        {/* Children */}
        <AnimatePresence>
          {isExpanded && hasChildren && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              {collection.children.map(child => renderCollection(child, level + 1))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  };
  
  const tree = buildTree();
  
  const themeClasses = {
    minimalist: {
      bg: 'bg-white border-zinc-200',
      text: 'text-zinc-950',
      header: 'bg-zinc-50 border-zinc-200',
      button: 'hover:bg-zinc-100'
    },
    hypebeast: {
      bg: 'bg-zinc-900 border-zinc-800',
      text: 'text-white',
      header: 'bg-zinc-800 border-zinc-700',
      button: 'hover:bg-zinc-700'
    },
    curator: {
      bg: 'bg-amber-50 border-amber-200',
      text: 'text-amber-950',
      header: 'bg-amber-100/80 border-amber-200',
      button: 'hover:bg-amber-100'
    }
  };
  
  const themeClass = themeClasses[theme] || themeClasses.minimalist;
  
  // Collapsed state for desktop
  if (!isMobile && isCollapsed) {
    return (
      <div className={`w-12 border-r ${themeClass.bg} p-2`}>
        <Button
          variant="ghost"
          size="sm"
          data-testid="expand-collections-nav"
          onClick={() => setIsCollapsed(false)}
          className={`w-full ${themeClass.button}`}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>
    );
  }
  
  // Mobile hamburger button
  if (isMobile && !isOpen) {
    return (
      <Button
        data-testid="open-collections-nav"
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 right-4 z-50 rounded-full w-14 h-14 shadow-lg"
      >
        <Menu className="w-6 h-6" />
      </Button>
    );
  }
  
  return (
    <>
      {/* Overlay for mobile */}
      {isMobile && isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
      
      {/* Navigation Panel */}
      <motion.div
        initial={isMobile ? { x: '-100%' } : false}
        animate={isMobile ? { x: 0 } : {}}
        exit={isMobile ? { x: '-100%' } : {}}
        className={`
          ${isMobile ? 'fixed left-0 top-0 h-full z-50' : 'relative'}
          w-64 border-r ${themeClass.bg} flex flex-col
        `}
      >
        {/* Header */}
        <div className={`p-4 border-b ${themeClass.header} flex items-center justify-between`}>
          <h3 className={`font-bold text-sm ${themeClass.text}`}>{navTitle}</h3>
          <div className="flex gap-1">
            {!isMobile && (
              <Button
                variant="ghost"
                size="sm"
                data-testid="collapse-collections-nav"
                onClick={() => setIsCollapsed(true)}
                className={`h-6 w-6 p-0 ${themeClass.button}`}
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            )}
            {isMobile && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsOpen(false)}
                className={`h-6 w-6 p-0 ${themeClass.button}`}
              >
                <X className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
        
        {/* All Products Option */}
        <div
          data-testid="public-collection-all"
          onClick={() => {
            onSelectCollection(null);
            if (isMobile) setIsOpen(false);
          }}
          className={`
            flex items-center gap-2 px-3 py-2 mx-2 mt-2 rounded-lg cursor-pointer transition-all
            ${selectedCollection === null ? themeClass.header : `hover:${themeClass.header}`}
          `}
        >
          <Folder className="w-4 h-4" />
          <span className={`text-sm font-medium ${themeClass.text}`}>{allProductsLabel}</span>
        </div>
        
        {/* Collections Tree */}
        <div className="flex-1 overflow-y-auto p-2">
          {tree.length === 0 ? (
            <div className="text-center py-8 px-4">
              <Folder className={`w-8 h-8 mx-auto mb-2 opacity-40 ${themeClass.text}`} />
              <p className={`text-xs opacity-60 ${themeClass.text}`}>No collections</p>
            </div>
          ) : (
            tree.map(collection => renderCollection(collection))
          )}
        </div>
      </motion.div>
    </>
  );
}
